import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { AsyncState } from '@/components/async';
import { Skeleton } from '@/components/feedback';
import {
  DashboardPropertyFilters,
  PropertyOverviewList,
  PropertySelectionProvider,
  type PropertyOverviewLayout,
  type PropertyTableDensity,
} from '@/components/dashboard/properties';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { useDashboardPropertyList } from '@/hooks/useDashboardProperties';
import {
  type DashboardPropertyFilterUpdater,
  useDashboardPropertyFilters,
} from '@/hooks/useDashboardPropertyFilters';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { PillButton } from '@/components/entity/PillButton';
import type {
  AssetType,
  PaginationInput,
  PropertyListSorting,
  PropertySortColumn,
} from '@/lib/types/property';
import { FilterModal } from '@/components/dashboard/filters/FilterModal';

const DESKTOP_QUERY = '(min-width: 1280px)';
const TABLET_QUERY = '(min-width: 768px)';
const DEFAULT_PAGE_SIZE = 25;
const ASSET_TYPE_ORDER: AssetType[] = ['industrial', 'flex', 'retail', 'office'];

export function resolveNextSort(
  current: PropertyListSorting | undefined,
  column: PropertySortColumn,
): PropertyListSorting {
  if (current?.column === column) {
    return {
      column,
      direction: current.direction === 'asc' ? 'desc' : 'asc',
    };
  }
  return {
    column,
    direction: 'asc',
  };
}

export function DashboardProperties(): JSX.Element {
  const user = useDashboardUser();
  const navigate = useNavigate();
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const tablet = useMediaQuery(TABLET_QUERY);
  const layout: PropertyOverviewLayout = desktop ? 'desktop' : tablet ? 'tablet' : 'mobile';
  const debugIdRef = useRef<string>(
    `dashboard-properties-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
  );
  const renderCountRef = useRef(0);

  const [pagination, setPagination] = useState<PaginationInput>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const {
    filters,
    appliedFilters,
    activeFilterCount,
    hasActiveFilters,
    filtersHash,
    updateFilters,
    resetFilters,
  } = useDashboardPropertyFilters();
  const [submarketOptions, setSubmarketOptions] = useState<string[]>([]);

  const [sort, setSort] = useState<PropertyListSorting>({
    column: 'propertyCode',
    direction: 'asc',
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [tableDensity, setTableDensity] = useState<PropertyTableDensity>(() => {
    if (typeof window === 'undefined') {
      return 'comfortable';
    }
    const stored = window.localStorage.getItem('dashboard_properties_density');
    return stored === 'compact' ? 'compact' : 'comfortable';
  });

  const listQuery = useDashboardPropertyList({
    user,
    filters: appliedFilters,
    pagination,
    sort,
  });
  const isInitialLoading = listQuery.status === 'pending';
  renderCountRef.current += 1;
  if (renderCountRef.current <= 5 || renderCountRef.current % 10 === 0) {
    console.debug('[dashboard.properties]', debugIdRef.current, 'render', renderCountRef.current, {
      layout,
      pagination,
      sort,
      filtersHash,
      hasActiveFilters,
      queryStatus: listQuery.status,
      isFetching: listQuery.isFetching,
    });
  }

  useEffect(() => {
    if (!listQuery.data) {
      return;
    }
    const safeTotalPages = Math.max(listQuery.data.totalPages, 1);
    if (pagination.page > safeTotalPages) {
      console.debug(
        '[dashboard.properties]',
        debugIdRef.current,
        'clamping page',
        pagination.page,
        'to',
        safeTotalPages,
      );
      setPagination((current) => ({
        ...current,
        page: safeTotalPages,
      }));
    }
  }, [listQuery.data, pagination.page]);

  useEffect(() => {
    if (!listQuery.data) {
      return;
    }
    const derived = listQuery.data.items
      .map((property) => property.submarket.trim())
      .filter((entry): entry is string => entry.length > 0);
    if (derived.length === 0) {
      return;
    }
    setSubmarketOptions((current) => {
      const nextSet = new Set(current);
      derived.forEach((entry) => {
        nextSet.add(entry);
      });
      const next = Array.from(nextSet).sort((a, b) => a.localeCompare(b));
      if (
        next.length === current.length &&
        next.every((value, index) => value === current[index])
      ) {
        return current;
      }
      console.debug(
        '[dashboard.properties]',
        debugIdRef.current,
        'updated submarket options',
        next,
      );
      return next;
    });
  }, [listQuery.data]);

  const metrics = useMemo(() => {
    const summary = listQuery.data?.metrics;
    if (!summary) {
      return [];
    }
    return [
      {
        label: 'Total properties',
        value: summary.total.toString(),
      },
      {
        label: 'With vacancy',
        value: summary.withVacancy.toString(),
      },
    ];
  }, [listQuery.data?.metrics]);

  const handleFiltersChange = (updater: DashboardPropertyFilterUpdater): void => {
    updateFilters(updater);
    setPagination((current) => ({
      ...current,
      page: 1,
    }));
  };

  const handleResetFilters = (): void => {
    resetFilters();
    setPagination((current) => ({
      ...current,
      page: 1,
    }));
  };

  const selectionScopeKey = `list-page-${String(pagination.page)}-${filtersHash}-${sort.column}-${sort.direction}`;

  const activeFilterLabel = hasActiveFilters
    ? `${activeFilterCount.toString()} active filter${activeFilterCount === 1 ? '' : 's'}`
    : 'No active filters';

  const handleSortChange = (column: PropertySortColumn) => {
    setSort((current) => resolveNextSort(current, column));
    setPagination((current) => ({
      ...current,
      page: 1,
    }));
  };

  useEffect(() => {
    console.debug('[dashboard.properties]', debugIdRef.current, 'state change', {
      pagination,
      sort,
      filtersHash,
      appliedFilters,
    });
  }, [appliedFilters, filtersHash, pagination, sort]);

  useEffect(() => {
    const querySummary = {
      status: listQuery.status,
      isFetching: listQuery.isFetching,
      isLoading: listQuery.isLoading,
      itemCount: listQuery.data?.items.length ?? 0,
      totalPages: listQuery.data?.totalPages ?? null,
      totalItems: listQuery.data?.totalItems ?? null,
      error: listQuery.error instanceof Error ? listQuery.error.message : null,
    };
    console.debug('[dashboard.properties]', debugIdRef.current, 'query update', querySummary);
  }, [
    listQuery.data?.items.length,
    listQuery.data?.totalItems,
    listQuery.data?.totalPages,
    listQuery.error,
    listQuery.isFetching,
    listQuery.isLoading,
    listQuery.status,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem('dashboard_properties_density', tableDensity);
  }, [tableDensity]);

  return (
    <PropertySelectionProvider enabled scopeKey={selectionScopeKey}>
      <section className="flex flex-col gap-xl py-8">
        <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-primary/70">
              Inventory overview
            </p>
            <h1 className="text-3xl font-semibold text-text-primary">Properties</h1>
            <p className="text-sm text-text-primary/70">
              Monitor availability, net / SF, additional / SF, and readiness states across every
              property. Layouts adapt per device and are verified on iOS before release.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <PillButton
              onClick={() => {
                navigate('/dashboard/properties/new');
              }}
            >
              New property
            </PillButton>
            <PillButton variant="secondary" disabled title="Bulk actions coming soon">
              Bulk actions
            </PillButton>
          </div>
        </header>

        {metrics.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {metrics.map((metric) => (
              <div
                key={metric.label}
                className="rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-sm text-text-primary shadow-sm"
              >
                <p className="text-xs uppercase tracking-wide text-text-primary/60">
                  {metric.label}
                </p>
                <p className="mt-1 text-2xl font-semibold">{metric.value}</p>
              </div>
            ))}
          </div>
        ) : isInitialLoading ? (
          <MetricsSkeleton />
        ) : null}

        <div className="dashboard-filter-bar z-30 flex flex-col gap-2 rounded-2xl border border-surface-subtle bg-white/90 px-4 py-3 shadow-sm backdrop-blur md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
              Filters
            </span>
            <span className="text-sm text-text-primary/80">{activeFilterLabel}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            <PillButton
              variant="secondary"
              size="sm"
              disabled={!hasActiveFilters}
              onClick={handleResetFilters}
            >
              Reset
            </PillButton>
            <div className="flex items-center gap-1 rounded-full border border-surface-subtle bg-white p-1">
              <button
                type="button"
                aria-pressed={tableDensity === 'comfortable'}
                onClick={() => {
                  setTableDensity('comfortable');
                }}
                className={clsx(
                  'min-h-9 rounded-full px-3 text-[0.65rem] font-semibold uppercase tracking-wide transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary',
                  tableDensity === 'comfortable'
                    ? 'bg-brand-primary text-white shadow-sm'
                    : 'text-text-primary/70 hover:bg-brand-primary/5',
                )}
              >
                Comfortable
              </button>
              <button
                type="button"
                aria-pressed={tableDensity === 'compact'}
                onClick={() => {
                  setTableDensity('compact');
                }}
                className={clsx(
                  'min-h-9 rounded-full px-3 text-[0.65rem] font-semibold uppercase tracking-wide transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary',
                  tableDensity === 'compact'
                    ? 'bg-brand-primary text-white shadow-sm'
                    : 'text-text-primary/70 hover:bg-brand-primary/5',
                )}
              >
                Compact
              </button>
            </div>
            <PillButton
              size="sm"
              onClick={() => {
                setFiltersOpen(true);
              }}
            >
              Open filters
            </PillButton>
          </div>
        </div>

        <FilterModal
          title="Filter properties"
          description="Refine readiness, rent, and availability without losing your place."
          trigger={null}
          activeFilterLabel={activeFilterLabel}
          hasActiveFilters={hasActiveFilters}
          onReset={handleResetFilters}
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
        >
          <DashboardPropertyFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onReset={handleResetFilters}
            activeFilterCount={activeFilterCount}
            hasActiveFilters={hasActiveFilters}
            submarketOptions={submarketOptions}
            assetTypeOptions={ASSET_TYPE_ORDER}
            showHeader={false}
          />
        </FilterModal>

        <AsyncState
          data={listQuery.data ?? null}
          isLoading={isInitialLoading}
          error={listQuery.error}
          skeleton={<PropertyGridSkeleton layout={layout} />}
        >
          {(result) => {
            return (
              <div className="flex flex-col gap-6">
                <PropertyOverviewList
                  properties={result.items}
                  layout={layout}
                  density={tableDensity}
                  pagination={{
                    page: result.page,
                    totalPages: result.totalPages,
                    totalItems: result.totalItems,
                    pageSize: result.pageSize,
                  }}
                  sort={sort}
                  onSortChange={handleSortChange}
                />
                <PropertyPaginationControls
                  page={result.page}
                  pageSize={result.pageSize}
                  totalItems={result.totalItems}
                  totalPages={result.totalPages}
                  onPageChange={(nextPage) => {
                    setPagination((current) => {
                      const safeTotalPages = Math.max(result.totalPages, 1);
                      const clamped = Math.min(Math.max(nextPage, 1), safeTotalPages);
                      if (clamped === current.page) {
                        return current;
                      }
                      return {
                        ...current,
                        page: clamped,
                      } satisfies PaginationInput;
                    });
                  }}
                />
              </div>
            );
          }}
        </AsyncState>
      </section>
    </PropertySelectionProvider>
  );
}

interface PropertyPaginationControlsProps {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function PropertyPaginationControls({
  page,
  pageSize,
  totalItems,
  totalPages,
  onPageChange,
}: PropertyPaginationControlsProps): JSX.Element {
  const safeTotalPages = Math.max(totalPages, 1);
  const hasResults = totalItems > 0;
  const start = hasResults ? (page - 1) * pageSize + 1 : 0;
  const end = hasResults ? Math.min(page * pageSize, totalItems) : 0;
  const startLabel = start.toLocaleString();
  const endLabel = end.toLocaleString();
  const totalLabel = totalItems.toLocaleString();
  const rangeLabel = hasResults ? `${startLabel}–${endLabel}` : '0';

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-sm text-text-primary shadow-sm sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-text-primary/80">
        {`Showing ${rangeLabel} of ${totalLabel} properties`}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="rounded-full border border-surface-subtle px-4 py-2 text-sm font-semibold text-text-primary/80 transition hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            onPageChange(page - 1);
          }}
          disabled={page <= 1}
        >
          Previous
        </button>
        <span className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
          Page {page} of {safeTotalPages}
        </span>
        <button
          type="button"
          className="rounded-full border border-surface-subtle px-4 py-2 text-sm font-semibold text-text-primary/80 transition hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            onPageChange(page + 1);
          }}
          disabled={page >= safeTotalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function PropertyGridSkeleton({ layout }: { layout: PropertyOverviewLayout }): JSX.Element {
  const rows = layout === 'desktop' ? 5 : 4;
  return (
    <div className="overflow-hidden rounded-3xl border border-surface-subtle bg-white shadow-sm">
      <div className="divide-surface-muted divide-y">
        {Array.from({ length: rows }).map((_item, index) => (
          <div key={index} className="bg-surface-muted/40 h-16 animate-pulse" />
        ))}
      </div>
    </div>
  );
}

function MetricsSkeleton(): JSX.Element {
  return (
    <div className="grid gap-4 md:grid-cols-2" aria-label="Loading property metrics">
      <Skeleton className="h-20 rounded-2xl" />
      <Skeleton className="h-20 rounded-2xl" />
    </div>
  );
}
